import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, XCircle, HelpCircle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/app/SEO";

const STATUS_LABEL = {
  pending_human_review: { label: "En attente de vérification", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  under_review: { label: "En cours d'examen", cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  missing_information: { label: "Complément demandé", cls: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200" },
};

export default function AdminReviewQueuePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailCase, setDetailCase] = useState(null);
  const [actionKind, setActionKind] = useState(null); // 'approve' | 'reject' | 'request_info'
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true); setError(null);
    api.get("/admin/cases/pending-review")
      .then(({ data }) => setRows(data || []))
      .catch((e) => setError(e?.response?.data?.detail || "Chargement impossible"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = rows.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (r.organization_name || "").toLowerCase().includes(s) ||
      (r._user?.email || "").toLowerCase().includes(s) ||
      (r.id || "").toLowerCase().includes(s)
    );
  });

  const openAction = (row, kind) => {
    setDetailCase(row);
    setActionKind(kind);
    setNote("");
  };
  const closeAction = () => { setDetailCase(null); setActionKind(null); setNote(""); };

  const submit = async () => {
    if (!detailCase || !actionKind) return;
    if (actionKind === "reject" && note.trim().length < 5) { toast.error("Motif de refus requis (5+ car.)"); return; }
    if (actionKind === "request_info" && note.trim().length < 5) { toast.error("Précisez le document/l'information demandé"); return; }
    setSubmitting(true);
    try {
      const body = actionKind === "request_info" ? { decision: "request_info", missing: note } : { decision: actionKind, note };
      await api.post(`/admin/cases/${detailCase.id}/review`, body);
      toast.success("Décision enregistrée");
      closeAction();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la décision");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-review-queue">
      <SEO title="Dossiers à vérifier" noindex />
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Dossiers à vérifier</h1>
        <Button variant="outline" size="sm" onClick={load} data-testid="review-refresh"><RotateCw className="h-4 w-4 mr-1" />Actualiser</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher (organisme, email, numéro…)"
          className="border border-border rounded-md px-3 py-2 text-sm bg-background min-w-[280px]"
          data-testid="review-search"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="review-status-filter">
          <option value="">Tous les statuts</option>
          <option value="pending_human_review">En attente</option>
          <option value="under_review">En cours d'examen</option>
          <option value="missing_information">Complément demandé</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>
      ) : error ? (
        <Card><CardContent className="p-6 text-center"><p className="text-sm text-muted-foreground mb-4">{error}</p><Button onClick={load}>Réessayer</Button></CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground" data-testid="review-empty">Aucun dossier à vérifier pour le moment (file vide).</CardContent></Card>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left px-4 py-2">Numéro</th>
                <th className="text-left px-4 py-2">Client</th>
                <th className="text-left px-4 py-2">Organisme</th>
                <th className="text-left px-4 py-2">Soumis le</th>
                <th className="text-left px-4 py-2">Statut</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const st = STATUS_LABEL[r.status] || { label: r.status, cls: "bg-secondary text-foreground" };
                return (
                  <tr key={r.id} className="border-t border-border" data-testid={`review-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{(r.id || "").slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-2">
                      <div>{r._user?.first_name || ""} {r._user?.last_name || ""}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r._user?.email || "—"}</div>
                    </td>
                    <td className="px-4 py-2">{r.organization_name || "—"}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleString("fr-FR") : "—"}</td>
                    <td className="px-4 py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openAction(r, "approve")} data-testid={`approve-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approuver</Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(r, "request_info")} data-testid={`request-${r.id}`}><HelpCircle className="h-3.5 w-3.5 mr-1" />Compléter</Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(r, "reject")} data-testid={`reject-${r.id}`}><XCircle className="h-3.5 w-3.5 mr-1" />Refuser</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailCase && actionKind && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-background rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <h2 className="text-xl font-semibold">
              {actionKind === "approve" && "Approuver le dossier"}
              {actionKind === "reject" && "Refuser le dossier"}
              {actionKind === "request_info" && "Demander un complément"}
            </h2>
            <div className="text-sm text-muted-foreground">
              Dossier <span className="font-mono">{detailCase.id.slice(0, 8).toUpperCase()}</span> — {detailCase.organization_name}
            </div>
            <div>
              <label htmlFor="review-note" className="block text-sm font-medium mb-1">
                {actionKind === "approve" && "Note interne (facultative)"}
                {actionKind === "reject" && "Motif de refus (obligatoire)"}
                {actionKind === "request_info" && "Document ou information demandé (obligatoire)"}
              </label>
              <Textarea id="review-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeAction} disabled={submitting} data-testid="review-cancel">Annuler</Button>
              <Button onClick={submit} disabled={submitting} className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="review-confirm">
                {submitting ? "Envoi…" : "Confirmer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
